import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import TerminalMockup from "@/components/TerminalMockup";
import PhoneMockup from "@/components/PhoneMockup";
import { SCENARIOS } from "@/components/TerminalMockup";
import WaitlistForm from "@/components/WaitlistForm";
import { Link } from "react-router-dom";

export default function HeroSection() {
  const [showNotification, setShowNotification] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [actionTaken, setActionTaken] = useState(null);
  const [isResumed, setIsResumed] = useState(false);

  const handleNeedInput = useCallback(() => {
    setTimeout(() => {
      setShowNotification(true);
      setTimeout(() => setShowActions(true), 600);
    }, 400);
  }, []);

  const handleAction = useCallback((action) => {
    setActionTaken(action);
    setTimeout(() => setIsResumed(true), 800);
  }, []);

  const scenarioData = SCENARIOS["approval_needed"];

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 pb-20 px-6 overflow-hidden"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 grid-bg opacity-40" />
      {/* Radial glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
            }}
          >
            <span
              className="text-xs uppercase tracking-wider"
              style={{
                color: "#F59E0B",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "10px",
              }}
            >
              Early Access
            </span>
            <span className="text-xs" style={{ color: "#71717A" }}>
              |
            </span>
            <span className="text-xs" style={{ color: "#A1A1AA" }}>
              Now accepting signups
            </span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-center tracking-tight leading-tight max-w-4xl mx-auto"
          style={{ fontFamily: "'Outfit', sans-serif", color: "#fff" }}
        >
          Run coding agents on your Mac.{" "}
          <span style={{ color: "#A1A1AA" }}>
            Stay in control from your phone.
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="text-base lg:text-lg text-center mt-6 max-w-2xl mx-auto leading-relaxed"
          style={{ color: "#A1A1AA" }}
        >
          Runzr lets you monitor, control, and respond to live Claude Code,
          Codex CLI, and terminal workflows from your phone — with
          notifications, approvals, and real-time session visibility.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10"
        >
          <div id="waitlist">
            <WaitlistForm variant="inline" />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="flex justify-center mt-4"
        >
          <Link
            to="/demo"
            data-testid="hero-demo-link"
            className="text-sm no-underline transition-colors duration-200"
            style={{ color: "#71717A" }}
            onMouseEnter={(e) => (e.target.style.color = "#fff")}
            onMouseLeave={(e) => (e.target.style.color = "#71717A")}
          >
            or try the interactive demo →
          </Link>
        </motion.div>

        {/* Product visual */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 relative"
        >
          <div className="relative flex justify-center items-end">
            {/* Terminal */}
            <div className="w-full max-w-2xl">
              <TerminalMockup
                scenario="approval_needed"
                onNeedInput={handleNeedInput}
                onComplete={() => {}}
                isResumed={isResumed}
              />
            </div>
            {/* Phone overlaid */}
            <div className="absolute -right-4 sm:right-4 lg:right-16 -bottom-8 transform scale-75 sm:scale-85 lg:scale-100 origin-bottom-right">
              <PhoneMockup
                notification={scenarioData.notification}
                actions={scenarioData.actions}
                onAction={handleAction}
                showNotification={showNotification}
                showActions={showActions}
                actionTaken={actionTaken}
              />
            </div>
          </div>
          {/* Glow under product visual */}
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at center, rgba(255,255,255,0.03) 0%, transparent 70%)",
            }}
          />
        </motion.div>
      </div>
    </section>
  );
}
